import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Tile, TileGrid, PreviewBanner } from "@/components/common/Tile";
import { Loading } from "@/components/common/Loading";
import { Pathogen } from "@/components/common/Pathogen";
import { useDevice } from "@/hooks/useDevice";
import { useOpticsForDevice, usePathogens } from "@/hooks/useOptic";
import type { OpticSummary, Pathogen as PathogenT } from "@/api/qpcr";

const LINE_COLORS = ["#2563EB", "#DC2626", "#7C3AED", "#059669", "#CA8A04", "#0EA5E9"];
const DAY_MS = 86_400_000;

const RANGES: { days: number; label: string }[] = [
  { days: 7,  label: "7d"  },
  { days: 14, label: "14d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

// We need ≥6 results in the most recent 30-day window before Trends are
// statistically meaningful enough to surface. Below that threshold we render
// an empty state instead.
const MIN_30D_RESULTS = 6;
const QUALIFY_WINDOW_DAYS = 30;

function dayKey(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function inWindow(o: OpticSummary, sinceMs: number): boolean {
  const t = new Date(o.captured_at ?? o.received_at).getTime();
  return !isNaN(t) && t >= sinceMs;
}

function buildTestsPerDay(optics: OpticSummary[]): { day: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const o of optics) {
    const k = dayKey(o.captured_at ?? o.received_at);
    if (!k) continue;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const keys = Array.from(counts.keys()).sort();
  if (keys.length === 0) return [];
  const start = new Date(keys[0]).getTime();
  const end = new Date(keys[keys.length - 1]).getTime();
  const out: { day: string; count: number }[] = [];
  for (let t = start; t <= end; t += DAY_MS) {
    const d = new Date(t);
    const k = d.toISOString().slice(0, 10);
    out.push({ day: `${d.getMonth() + 1}/${d.getDate()}`, count: counts.get(k) ?? 0 });
  }
  return out;
}

function buildMockConc(
  pathogenId: number,
  optics: OpticSummary[],
): { day: string; value: number }[] {
  const days = new Set<string>();
  for (const o of optics) {
    if (!o.pathogen_ids.includes(pathogenId)) continue;
    const k = dayKey(o.captured_at ?? o.received_at);
    if (k) days.add(k);
  }
  const sorted = Array.from(days).sort();
  return sorted.map(k => {
    const d = new Date(k);
    const t = d.getTime() / DAY_MS;
    const base = 1000 + (pathogenId * 137) % 400;
    const amp = 600 + (pathogenId * 53) % 300;
    const value = Math.max(0, Math.round(base + amp * Math.sin(t * 0.4 + pathogenId)));
    return { day: `${d.getMonth() + 1}/${d.getDate()}`, value };
  });
}

export default function Trends() {
  const { deviceId = "" } = useParams<{ deviceId: string }>();
  const deviceQ = useDevice(deviceId);
  // Pull a generous window so the 90d range has data to chart.
  const opticsQ = useOpticsForDevice(deviceId, 500);
  const pathogensQ = usePathogens();

  const [days, setDays] = useState<number>(30);

  const allOptics: OpticSummary[] = opticsQ.data?.optics ?? [];
  const allPathogens: PathogenT[] = pathogensQ.data?.pathogens ?? [];

  // Qualifier window is always the last 30 days regardless of the user's
  // chosen display range — the threshold is about whether trends are
  // statistically meaningful for *this device*, not the current selection.
  const qualifyingCount = useMemo(() => {
    const since = Date.now() - QUALIFY_WINDOW_DAYS * DAY_MS;
    return allOptics.filter(o => inWindow(o, since)).length;
  }, [allOptics]);

  const optics = useMemo(() => {
    const since = Date.now() - days * DAY_MS;
    return allOptics.filter(o => inWindow(o, since));
  }, [allOptics, days]);

  const testsPerDay = useMemo(() => buildTestsPerDay(optics), [optics]);

  const presentPathogens = useMemo(() => {
    const ids = new Set<number>();
    for (const o of optics) o.pathogen_ids.forEach(id => ids.add(id));
    return allPathogens.filter(p => ids.has(p.id)).slice(0, 3);
  }, [optics, allPathogens]);

  if (deviceQ.isLoading || opticsQ.isLoading || pathogensQ.isLoading) {
    return <Loading />;
  }

  const qualifies = qualifyingCount >= MIN_30D_RESULTS;

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-end justify-between gap-md">
        <div>
          <h1 className="text-2xl font-semibold text-text leading-tight">Trends</h1>
          <p className="text-sm text-textLight">
            Aggregated activity and per-pathogen concentration trends.
          </p>
        </div>
        <div className="inline-flex rounded-md border border-border bg-surface overflow-hidden text-sm">
          {RANGES.map(r => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              disabled={!qualifies}
              className={`px-md py-xs cursor-pointer ${
                days === r.days ? "bg-brand text-white" : "text-text hover:bg-brand/5"
              } ${qualifies ? "" : "opacity-40 cursor-not-allowed"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!qualifies ? (
        <Tile>
          <div className="flex flex-col items-center justify-center py-xl text-center gap-xs">
            <div className="text-sm font-medium text-text">
              Not enough data for trends yet
            </div>
            <div className="text-xs text-textLight">
              Trends require at least {MIN_30D_RESULTS} results in the last {QUALIFY_WINDOW_DAYS} days.
              Currently this device has {qualifyingCount}.
            </div>
          </div>
        </Tile>
      ) : (
        <>
          <TileGrid>
            <div className="col-span-12">
              <Tile title="Tests Performed per Day">
                {testsPerDay.length === 0 ? (
                  <div className="flex items-center justify-center py-lg text-sm text-textLight">
                    No tests in this window.
                  </div>
                ) : (
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={testsPerDay} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="day" stroke="#6B7280" fontSize={11} minTickGap={20} />
                        <YAxis allowDecimals={false} stroke="#6B7280" fontSize={11} width={36} />
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                        <Bar dataKey="count" fill="#2563EB" isAnimationActive={false} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Tile>
            </div>
          </TileGrid>

          <PreviewBanner>
            Per-pathogen concentration trends require quantification fields on the backend.
          </PreviewBanner>

          <TileGrid>
            {presentPathogens.length === 0 ? (
              <div className="col-span-12">
                <Tile title="Pathogen concentration trends">
                  <div className="flex items-center justify-center py-lg text-sm text-textLight">
                    No pathogen tests in this window.
                  </div>
                </Tile>
              </div>
            ) : (
              presentPathogens.map((p, i) => {
                const data = buildMockConc(p.id, optics);
                return (
                  <div key={p.id} className="col-span-12 md:col-span-6 lg:col-span-4">
                    <Tile
                      title={
                        <span>
                          <Pathogen name={p.name} />
                          <span className="text-textLight"> · {p.unit || "copies/mL"}</span>
                        </span>
                      }
                    >
                      <div className="h-56 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="day" stroke="#6B7280" fontSize={10} minTickGap={20} />
                            <YAxis stroke="#6B7280" fontSize={10} width={52} />
                            <Tooltip contentStyle={{ fontSize: 12 }} />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke={LINE_COLORS[i % LINE_COLORS.length]}
                              strokeWidth={2}
                              dot={false}
                              isAnimationActive={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </Tile>
                  </div>
                );
              })
            )}
          </TileGrid>
        </>
      )}
    </div>
  );
}
